import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { addCustomCommentToRequestBody } from '@/components/banners/BannerFinancingOffer/components/BannerFinancingOfferForm/helpers'
import { bannerFinancingOrderFormAdapter } from '@/components/banners/BannerFinancingOffer/helpers'
import { messages } from '@/components/banners/BannerFinancingOffer/messages'
import { useApplyLostFinancingOffer } from '@/components/composed/financing/hooks'
import { useApplyMFOFinancingOffer } from '@/components/composed/financing/hooks/useApplyMFOFinancingOffer'
import { CustomizedForm } from '@/components/composed/forms'
import { useApplyingOfferFormSteps } from '@/components/composed/landingBlocks/deprecated/LandingApplyingOfferForm/useApplyingOfferFormSteps'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { FormValuesFormatter } from '@/constants/financing'
import { isUserAgrarian } from '@/logic/auth/reducer'
import type { IApplyingFinancingOfferRequestBody, IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferRoute, EFinancingOfferType } from '@/types'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { formMessages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
})

export const BannerFinancingOfferForm: React.FC = () => {
  const { isAgrarian } = useSelector(mapState)
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const shouldDisableButton = isAuthenticated && !isAgrarian
  const history = useHistory()
  const { applyLostFinancingOffer } = useApplyLostFinancingOffer()

  const {
    setOptions,
    formValues: values,
    profile,
  } = useApplyingFinancingOfferForm({
    productId: FundingProduct.EXPRESS_MFO,
    offerType: EFinancingOfferType.EXPRESS_MFO,
    isCustomFormattedRequestBody: true,
  })
  const { applyMFOFianncingOffer } = useApplyMFOFinancingOffer()

  const steps = useApplyingOfferFormSteps(EFinancingOfferType.EXPRESS_MFO, isAuthenticated)

  const handleSubmitForAuthenticatedUser = async (formValues: IFinancingOfferFormValues) => {
    const body = FormValuesFormatter[EFinancingOfferType.EXPRESS_MFO]?.(formValues) || formValues

    if (!isApplyingOfferRequestBody(body)) {
      return
    }

    const requestBody = addCustomCommentToRequestBody(body, formMessages.comment)

    await applyMFOFianncingOffer({ body: requestBody, withSuccessModal: true })
  }

  const handleCloseAuthDialog = (formValues: IFinancingOfferFormValues) => () => {
    const formattedFormValues = addCustomCommentToRequestBody(formValues, formMessages.comment)
    setOptions({ formValues: formattedFormValues })
  }

  const handleCloseAuthModal = (body: IFinancingOfferFormValues | IApplyingFinancingOfferRequestBody) => () => {
    const requestBody = addCustomCommentToRequestBody(body, formMessages.comment)

    if (isApplyingOfferRequestBody(requestBody)) {
      applyLostFinancingOffer({ body: requestBody, offerType: EFinancingOfferType.EXPRESS_MFO })
    }
  }

  const handleSubmitForNonAuthenticatedUser = (formValues: IFinancingOfferFormValues) => {
    const body = FormValuesFormatter[EFinancingOfferType.EXPRESS_MFO]?.(formValues) || formValues

    setOptions({ formValues: {} })

    openAuthModal({
      shouldSendCodeImmediately: true,
      initialValues: { phone: formValues.phone, email: formValues.email },
      onCloseDialog: handleCloseAuthDialog(formValues),
      onCloseModal: handleCloseAuthModal(body),
    })
  }

  const handleSubmit = async (formValues: IFinancingOfferFormValues) => {
    if (isAuthenticated) {
      await handleSubmitForAuthenticatedUser(formValues)
    } else {
      await handleSubmitForNonAuthenticatedUser(formValues)
    }
  }

  const onMoreClick = useCallback(() => {
    history.push(EFinancingOfferRoute.MFOPage)
  }, [history])

  const stepsAdapter = bannerFinancingOrderFormAdapter(steps, onMoreClick)

  return (
    <div className="bannerFinancingOfferForm">
      <CustomizedForm<IFinancingOfferFormValues>
        initialValues={getApplyingFinancingOfferFormInitialValues({
          offerType: EFinancingOfferType.EXPRESS_MFO,
          productId: FundingProduct.EXPRESS_MFO,
          profile,
          formValues: values,
        })}
        onSubmit={handleSubmit}
        steps={stepsAdapter}
        // TODO: убрать хранимое в redux состояние запроса https://jira.poleinvest.ru/browse/IFA-2085
        requestStatusName="mockedRequestStatus"
        tooltipText={messages.hint.forAgrariansOnly}
        shouldDisableButton={shouldDisableButton}
        ignoreValidationForSubmitButton
        shouldShowRequiringFieldsLabel={false}
        shouldUseLocalRequestState
      />
    </div>
  )
}
